import { forwardRef } from "react";

import { cn } from "@lib/utils";

import { TSizeText, sizeClasses } from "./Typography";

export type TLinkColor = "primary" | "secondary" | "success" | "error" | "info" | "textPrimary" | "textSecondary";

export type TLinkUnderline = "always" | "hover" | "none";

const colorClasses: Record<TLinkColor, string> = {
	primary: "text-[var(--color-primary)]",
	secondary: "text-[var(--color-secondary)]",
	success: "text-[var(--color-success)]",
	error: "text-[var(--color-error)]",
	info: "text-[var(--color-info)]",
	textPrimary: "text-white",
	textSecondary: "text-[var(--color-msg-time)]",
};

const underlineClasses: Record<TLinkUnderline, string> = {
	always: "underline",
	hover: "no-underline hover:underline",
	none: "no-underline",
};

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
	/** Override the root element (e.g. React Router Link) */
	component?: React.ElementType;
	color?: TLinkColor;
	underline?: TLinkUnderline;
	size?: TSizeText;
}

/** Used to provide accessible navigation
 * @prop component - The component to use for the link.
 * @prop color - The color of the link.
 * @prop underline - The underline of the link.
 * @prop size - The size of the link.
 * @prop className - The class name to apply to the link.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
	(
		{ component: Component = "a", color = "primary", underline = "always", size = "base", className, ...props },
		ref,
	) => (
		<Component
			ref={ref}
			className={cn(
				colorClasses[color],
				underlineClasses[underline],
				sizeClasses[size],
				"outline-none hover:opacity-60",
				className,
			)}
			{...props}
		/>
	),
);

Link.displayName = "Link";
